import { useEffect, useState } from 'react';
import instagramPosts, { instagramProfile } from '../instagramPosts';

const postSlides = (post) =>
    (post.images && post.images.length ? post.images : [post.image]).filter(Boolean);

const postAlt = (post, username = instagramProfile.username) =>
    post.alt
    || post.caption
    || `Instagram post from @${username}`;

const formatDate = (timestamp) => {
    if (!timestamp) {
        return '';
    }
    return new Date(timestamp).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
    });
};

const CloseIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M6.4 6.4l11.2 11.2M17.6 6.4L6.4 17.6" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </svg>
);

const ChevronIcon = ({ direction }) => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        {direction === 'prev' ? (
            <path d="M14.5 5.5L8.5 12l6 6.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        ) : (
            <path d="M9.5 5.5L15.5 12l-6 6.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        )}
    </svg>
);

const InstagramIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3.2" y="3.2" width="17.6" height="17.6" rx="5" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="12" cy="12" r="4.1" fill="none" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="17.15" cy="6.85" r="1" fill="currentColor" />
    </svg>
);

const FEED_LIMIT = 6;

const InstagramFeed = ({ className = '' }) => {
    const fallbackPosts = instagramPosts.filter((post) => postSlides(post).length).slice(0, FEED_LIMIT);
    const [posts, setPosts] = useState(fallbackPosts);
    const [profile, setProfile] = useState(instagramProfile);
    const [openIndex, setOpenIndex] = useState(null);
    const [slideIndex, setSlideIndex] = useState(0);

    useEffect(() => {
        let cancelled = false;
        fetch('/api/instagram')
            .then((response) => (response.ok && response.status !== 204 ? response.json() : Promise.reject()))
            .then((data) => {
                if (cancelled || !Array.isArray(data.posts) || !data.posts.length) {
                    return;
                }
                setProfile({
                    username: data.profile?.username || instagramProfile.username,
                    url: data.profile?.url || instagramProfile.url,
                    image: data.profile?.image || instagramProfile.image,
                });
                setPosts(data.posts.filter((post) => postSlides(post).length).slice(0, FEED_LIMIT));
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    const openPost = (index) => {
        setSlideIndex(0);
        setOpenIndex(index);
    };

    const closePost = () => {
        setOpenIndex(null);
        setSlideIndex(0);
    };

    const stepPost = (direction) => {
        setOpenIndex((current) => {
            if (current === null || !posts.length) {
                return current;
            }
            return (current + direction + posts.length) % posts.length;
        });
        setSlideIndex(0);
    };

    useEffect(() => {
        if (openIndex === null) {
            return undefined;
        }
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const total = posts.length;

        const onKey = (event) => {
            if (event.key === 'Escape') {
                setOpenIndex(null);
                setSlideIndex(0);
            }
            if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
                const direction = event.key === 'ArrowLeft' ? -1 : 1;
                setOpenIndex((current) => {
                    if (current === null || !total) {
                        return current;
                    }
                    return (current + direction + total) % total;
                });
                setSlideIndex(0);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', onKey);
        };
    }, [openIndex, posts.length]);

    if (!posts.length) {
        return null;
    }

    const openPostData = openIndex === null ? null : posts[openIndex];
    const slides = openPostData ? postSlides(openPostData) : [];
    const activeSlide = slides[Math.min(slideIndex, Math.max(slides.length - 1, 0))];

    return (
        <section className={`homeInstagram ${className}`.trim()} aria-label="Instagram">
            <header className="homeInstagramHeader">
                <p className="homeInstagramKicker">Instagram</p>
                <a
                    className="homeInstagramHandle"
                    href={profile.url}
                    target="_blank"
                    rel="noreferrer"
                >
                    @{profile.username}
                </a>
            </header>
            <div className="homeInstagramGrid">
                {posts.map((post, index) => (
                    <button
                        type="button"
                        className="homeInstagramPost"
                        onClick={() => openPost(index)}
                        key={post.id || post.permalink || index}
                    >
                        <img src={postSlides(post)[0]} alt={postAlt(post, profile.username)} />
                    </button>
                ))}
            </div>
            {openPostData ? (
                <div className="igLightbox" role="presentation" onClick={closePost}>
                    <button
                        type="button"
                        className="igLightboxClose"
                        onClick={closePost}
                        aria-label="Close post"
                    >
                        <CloseIcon />
                    </button>
                    {posts.length > 1 ? (
                        <button
                            type="button"
                            className="igLightboxNav igLightboxNavPrev"
                            onClick={(event) => {
                                event.stopPropagation();
                                stepPost(-1);
                            }}
                            aria-label="Previous post"
                        >
                            <ChevronIcon direction="prev" />
                        </button>
                    ) : null}
                    {posts.length > 1 ? (
                        <button
                            type="button"
                            className="igLightboxNav igLightboxNavNext"
                            onClick={(event) => {
                                event.stopPropagation();
                                stepPost(1);
                            }}
                            aria-label="Next post"
                        >
                            <ChevronIcon direction="next" />
                        </button>
                    ) : null}
                    <article
                        className="igLightboxBox"
                        role="dialog"
                        aria-modal="true"
                        aria-label={`Instagram post by @${profile.username}`}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="igLightboxMedia">
                            {openPostData.video && slideIndex === 0 ? (
                                <video
                                    src={openPostData.video}
                                    poster={activeSlide}
                                    controls
                                    playsInline
                                />
                            ) : (
                                <img src={activeSlide} alt={postAlt(openPostData, profile.username)} />
                            )}
                            {slides.length > 1 ? (
                                <>
                                    <button
                                        type="button"
                                        className="igLightboxSlide igLightboxSlidePrev"
                                        onClick={() => setSlideIndex((current) => (current - 1 + slides.length) % slides.length)}
                                        aria-label="Previous photo"
                                    >
                                        <ChevronIcon direction="prev" />
                                    </button>
                                    <button
                                        type="button"
                                        className="igLightboxSlide igLightboxSlideNext"
                                        onClick={() => setSlideIndex((current) => (current + 1) % slides.length)}
                                        aria-label="Next photo"
                                    >
                                        <ChevronIcon direction="next" />
                                    </button>
                                    <div className="igLightboxDots">
                                        {slides.map((slide, index) => (
                                            <button
                                                type="button"
                                                className={`igLightboxDot${index === slideIndex ? ' igLightboxDotActive' : ''}`}
                                                key={slide}
                                                onClick={() => setSlideIndex(index)}
                                                aria-label={`Photo ${index + 1}`}
                                            />
                                        ))}
                                    </div>
                                </>
                            ) : null}
                        </div>
                        <div className="igLightboxPanel">
                            <a
                                className="igLightboxProfile"
                                href={profile.url}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {profile.image ? (
                                    <img src={profile.image} alt="" />
                                ) : null}
                                <span>@{profile.username}</span>
                            </a>
                            {openPostData.caption ? (
                                <p className="igLightboxCaption">{openPostData.caption}</p>
                            ) : null}
                            {openPostData.timestamp ? (
                                <div className="igLightboxMeta">
                                    <p className="igLightboxDate">{formatDate(openPostData.timestamp)}</p>
                                </div>
                            ) : null}
                            {openPostData.permalink ? (
                                <a
                                    className="igLightboxLink"
                                    href={openPostData.permalink}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <InstagramIcon />
                                    View on Instagram
                                </a>
                            ) : null}
                        </div>
                    </article>
                </div>
            ) : null}
        </section>
    );
};

export default InstagramFeed;
